import { createContext, useContext, useEffect, useRef, useState } from 'react';

// ─── Theme Mode Definitions ──────────────────────────────────────────────────

export type ThemeMode = 'dark' | 'light';

interface ThemeContextValue {
  theme: ThemeMode;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
  isAutoTheme: boolean; // true when following IST time automatically
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY    = 'nc_main_theme';
const STORAGE_DATE   = 'nc_main_theme_date'; // IST date when pref was saved
const IST_OFFSET_MS  = 5.5 * 60 * 60 * 1000; // UTC+5:30

// ─── IST Helpers ─────────────────────────────────────────────────────────────

/** Returns current hour (0–23) in Indian Standard Time */
function getISTHour(): number {
  const nowUTC = Date.now();
  const istDate = new Date(nowUTC + IST_OFFSET_MS);
  return istDate.getUTCHours();
}

/** Returns current date string (YYYY-MM-DD) in IST */
function getISTDateString(): string {
  const nowUTC = Date.now();
  const istDate = new Date(nowUTC + IST_OFFSET_MS);
  return istDate.toISOString().slice(0, 10);
}

/**
 * Day = 06:00–20:59 IST → light
 * Night = 21:00–05:59 IST → dark
 */
function getAutoTheme(): ThemeMode {
  const h = getISTHour();
  return h >= 6 && h < 21 ? 'light' : 'dark';
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isAutoTheme, setIsAutoTheme] = useState(false);

  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const stored     = localStorage.getItem(STORAGE_KEY);
      const storedDate = localStorage.getItem(STORAGE_DATE);
      const todayIST   = getISTDateString();

      // If user manually chose a theme today, respect it
      if (storedDate === todayIST && (stored === 'light' || stored === 'dark')) {
        return stored;
      }

      // Stale pref from a previous day — clear it and auto-detect
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_DATE);
    } catch {
      // ignore storage errors
    }
    // No saved pref → auto-detect from IST hour
    return getAutoTheme();
  });

  // Track whether theme is currently following auto-IST (no manual override today)
  useEffect(() => {
    try {
      const storedDate = localStorage.getItem(STORAGE_DATE);
      const todayIST   = getISTDateString();
      setIsAutoTheme(storedDate !== todayIST);
    } catch {
      setIsAutoTheme(true);
    }
  }, []);

  // Re-check IST time every minute — auto-switch at 06:00 and 21:00 boundaries
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    timerRef.current = setInterval(() => {
      try {
        const storedDate = localStorage.getItem(STORAGE_DATE);
        const todayIST   = getISTDateString();

        // If user hasn't manually overridden today, keep following auto
        if (storedDate !== todayIST) {
          setThemeState(getAutoTheme());
          setIsAutoTheme(true);
        }
      } catch {
        setThemeState(getAutoTheme());
      }
    }, 60_000); // check every 60 s

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const setTheme = (mode: ThemeMode) => {
    // Manual override — persist for today
    try {
      localStorage.setItem(STORAGE_KEY,  mode);
      localStorage.setItem(STORAGE_DATE, getISTDateString());
    } catch { /* ignore */ }
    setIsAutoTheme(false);
    setThemeState(mode);
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Sync data-theme attribute on <html> + .dark / .light class
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
  }, [theme]);

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme, setTheme, isAutoTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
